<template>
  <content-dialog
    v-if="!!event"
    ref="dialog"
    v-model="dialogOpen"
    v-bind="$attrs"
    fullscreen-mobile
    no-backdrop-dismiss
    width="1150px"
    height="95vh"
    max-height="1000px"
    card-class="flex no-wrap"
    :class="{'appointment-can-peek': enablePeeking, 'is-peeking': enablePeeking && isPeeking}"
    :seamless="isPeeking"
    :card-visible="!!event"
    :loading="checkingAvailability || loading"
    :tags="[helpCodes.createAppointment, helpCodes.scheduler, helpCodes.appointments]"
    @hide="$emit('hide')"
  >
    <template #card>
      <two-panel-split
        with-close
        with-list-toolbar
        list-width="60%"
        list-width-when-viewing="60%"
        toolbar-anchor="none"
        list-classes="flex items-stretch column no-wrap"
        :view-classes="{'flex items-stretch column no-wrap overflow-hidden-x': !!selectedTab}"
        :title="`${modeTitle} Appointment`"
        :is-viewing="!isPeeking && !!selectedTab"
        @close="$refs.dialog.hide()"
        @view-back="selectedTab = null"
      >
        <!-- Slim toolbar to display if dialog is in peek mode -->
        <template v-if="isPeeking" #list-toolbar="{ stickyClasses }">
          <q-toolbar style="z-index: 3;" :class="stickyClasses">
            <q-btn
              flat
              dense
              round
              icon="close"
              @click="$refs.dialog.hide()"
            />

            <q-space />

            <q-btn
              rounded
              no-caps
              color="blue-7"
              label="Expand New Appointment"
              icon="mdi-chevron-up"
              class="absolute-center"
              style="width: 250px;"
              @click="isPeeking = !isPeeking"
            />

            <q-space />
          </q-toolbar>
        </template>

        <template #list-toolbar-inner>
          <q-btn
            v-if="enablePeeking"
            dense
            rounded
            no-caps
            flat
            icon-right="mdi-magnify-expand"
            class="q-mr-sm"
            :label="windowWidth > 450 ? 'Peek' : undefined"
            @click="isPeeking = !isPeeking"
          >
            <q-tooltip>Peek to Calendar</q-tooltip>
          </q-btn>

          <help-button />
        </template>

        <template #list="{ small }">
          <div v-if="!isPeeking" class="flex-auto">
            <q-linear-progress
              v-if="small"
              :value="stepPercentageDone"
              stripe
              class="q-mb-sm"
            />

            <OverviewPartial
              :event="event"
              :name-spacing="170"
              :mode="mode"
              :business-hours="businessHours"
              :errors="errors"
              :selected-tab="selectedTab"
              :can-alter-reminders="canAlterReminders"
              :has-updated-rule="hasUpdatedRule"
              :has-deleted-rule="hasDeletedRule"
              :has-future-rule="hasFutureRule"
              @tab="selectedTab = $event"
              @update-reference="$emit('updateReference')"
            />

            <!-- Details -->
            <AppointmentPartial
              :mode="mode"
              :event="event"
              :errors="errors"
              :business-hours="businessHours"
              :name-spacing="170"
              :has-updated-rule="hasUpdatedRule"
              :has-deleted-rule="hasDeletedRule"
              :has-future-rule="hasFutureRule"
              :selected-tab="selectedTab"
              @tab="selectedTab = $event"
              @update-reference="$emit('updateReference')"
            />
          </div>

          <q-toolbar
            v-if="!isPeeking"
            id="appt-save-toolbar"
            class="sticky-position from-page-bottom blurred-container"
          >
            <q-checkbox
              v-if="hasClients"
              v-model="sendConfirmationEmail"
              label="Send confirmation"
              dense
              :disable="!canSendConfirmationEmail"
              class="q-pl-md q-mr-md"
            >
              <q-tooltip v-if="!canSendConfirmationEmail">
                Confirmations can only be sent for scheduled appointments
              </q-tooltip>
            </q-checkbox>
            <q-space />
            <q-btn
              color="green"
              icon-right="check"
              label="Save"
              :loading="checkingAvailability || loading"
              @click="save"
            />
          </q-toolbar>
        </template>

        <template #view="{ back, small }">
          <div class="flex-auto">
            <q-linear-progress
              v-if="small && currentTabIsStepped"
              :value="stepPercentageDone"
              stripe
              class="q-mb-sm"
            />

            <q-tab-panels
              v-model="selectedTab"
              class="overflow-panels"
              :animated="small"
            >
              <!-- Appointment Types -->
              <q-tab-panel name="types" class="q-pa-none">
                <TypesPartial
                  :event="event"
                  :errors="errors"
                  :prefill-date="prefillDate"
                  class="q-mb-lg"
                />
              </q-tab-panel>

              <!-- Clients -->
              <q-tab-panel name="clients" class="q-pa-none">
                <ClientsPartial
                  :event="event"
                  :errors="errors"
                  :without-client="withoutClient"
                  :can-alter-reminders="canAlterReminders"
                  class="q-mb-lg"
                />
              </q-tab-panel>

              <!-- Staff Members -->
              <q-tab-panel name="staff" class="q-pa-none">
                <StaffPartial
                  :mode="mode"
                  :event="event"
                  :errors="errors"
                  :can-alter-reminders="canAlterReminders"
                  class="q-mb-lg"
                  @check-availability="$refs.availabilityDialog.open()"
                />
              </q-tab-panel>

              <!-- Color -->
              <q-tab-panel name="color" class="q-pa-none">
                <ColorPartial
                  :event="event"
                  :errors="errors"
                />
              </q-tab-panel>

              <!-- Required Templates -->
              <q-tab-panel name="required-forms" class="q-pa-none q-pt-sm">
                <q-list>
                  <q-item-wrapper
                    icon="mdi-clipboard-check-outline"
                    label="Templates that must be completed before this appointment can be marked as complete."
                    caption="Each required template automatically becomes a task in this appointment's checklist. The task ticks off once its report is done, and can only be removed by un-requiring the template here."
                    label-class="text-subtitle2"
                    dense
                    class="q-mb-sm invisible-item"
                  />

                  <RequiredFormSelector
                    v-model="event.required_form_template_ids"
                    :required-form-templates="event.required_form_templates"
                    @form-added="onRequiredFormAdded"
                    @form-removed="onRequiredFormRemoved"
                  />
                </q-list>
              </q-tab-panel>

              <!-- Checklist Templates -->
              <q-tab-panel name="checklist-templates" class="q-pa-none q-pt-sm">
                <q-list>
                  <q-item-wrapper
                    icon="mdi-format-list-checks"
                    label="Checklists that will be attached to this appointment."
                    label-class="text-subtitle2"
                    dense
                    class="q-mb-sm invisible-item"
                  />

                  <ChecklistTemplateSelector
                    :checklist-template-ids="event.checklist_template_ids"
                    :checklist-templates="event.checklist_templates"
                    @update:checklist-template-ids="(val) => event.checklist_template_ids = val"
                    @update:checklist-templates="(val) => event.checklist_templates = val"
                  />

                  <q-separator spaced />

                  <q-item-wrapper
                    icon="mdi-clipboard-list-outline"
                    label="Current tasks on this appointment."
                    label-class="text-subtitle2"
                    dense
                    class="q-mb-sm invisible-item"
                  />

                  <template v-if="mode !== 'add' && event.id">
                    <q-list
                      v-if="currentTasks.length > 0"
                      separator
                    >
                      <q-item
                        v-for="task in currentTasks"
                        :key="'task-' + task.id"
                      >
                        <q-item-section side>
                          <q-icon
                            :name="task.pending ? 'mdi-plus-circle-outline' : (task.is_completed ? 'mdi-checkbox-marked-outline' : 'mdi-checkbox-blank-outline')"
                            :color="task.pending ? 'blue-grey-5' : (task.is_completed ? 'green-7' : 'grey-6')"
                          />
                        </q-item-section>
                        <q-item-section>
                          <q-item-label
                            :class="{
                              'text-strike text-grey-5': task.is_completed,
                              'text-grey-6 text-italic': task.pending
                            }"
                          >
                            {{ task.text }}
                          </q-item-label>
                          <q-item-label caption>
                            {{ task.checklistName }}
                          </q-item-label>
                          <div
                            v-if="taskRequirements(task).length > 0"
                            class="row items-center q-gutter-x-sm q-mt-xs"
                          >
                            <span
                              v-for="(req, reqIndex) in taskRequirements(task)"
                              :key="'req-' + task.id + '-' + reqIndex"
                              class="row inline items-center no-wrap"
                            >
                              <q-icon
                                :name="req.icon"
                                color="grey-7"
                                size="15px"
                                class="q-mr-xs"
                              />
                              <span class="text-caption text-grey-7">{{ req.label }}</span>
                            </span>
                          </div>
                          <q-item-label
                            v-if="task.pending"
                            caption
                            class="text-blue-grey-7"
                          >
                            <q-icon name="mdi-link-variant" size="12px" />
                            Will be added as a task when the appointment is saved — this template is required above
                          </q-item-label>
                          <q-item-label
                            v-else-if="task.fromRequiredForm"
                            caption
                            class="text-blue-grey-7"
                          >
                            <q-icon name="mdi-link-variant" size="12px" />
                            Added automatically — this template is required on the appointment
                          </q-item-label>
                        </q-item-section>
                      </q-item>
                    </q-list>
                    <div
                      v-else
                      class="text-caption text-grey-6 q-px-md q-py-sm"
                    >
                      No tasks on this appointment yet
                    </div>

                    <clickable-item
                      label="Add task..."
                      @click="$refs.addTaskDialog.open()"
                    />
                  </template>
                  <div
                    v-else
                    class="text-caption text-grey-6 q-px-md q-py-sm"
                  >
                    Individual tasks can be added once the appointment has been saved
                  </div>
                </q-list>
              </q-tab-panel>
            </q-tab-panels>
          </div>

          <q-toolbar
            v-if="small && nextStep"
            class="sticky-position from-page-bottom blurred-container self-end"
          >
            <q-space />
            <q-btn
              v-if="nextStep"
              color="blue"
              icon-right="mdi-arrow-right"
              size="12px"
              :label="nextStep.title"
              @click="selectedTab = nextStep.name"
            />
            <q-space />
          </q-toolbar>
        </template>

        <template v-if="currentTab" #view-toolbar-after="{ small }">
          <page-toolbar-title>
            <template v-if="small" #subtitle>
              {{ modeTitle }} Appointment
            </template>
            {{ currentTab.title }}
          </page-toolbar-title>
          <help-button v-if="small" />
        </template>

        <template v-if="!isPeeking" #view-empty>
          <q-icon
            name="mdi-calendar-edit"
            size="7em"
            color="grey-5"
          />
        </template>
      </two-panel-split>
    </template>
  </content-dialog>

  <AvailabilityDialog
    v-if="!!event"
    :id="event.id"
    ref="availabilityDialog"
    :attendees="event.attendees"
    :start-date="event.start_date"
    :end-date="event.end_date"
    :timezone="event.timezone"
    :is-scheduled="event.status !== 'UNSCHEDULED'"
    :all-day="event.all_day"
    with-editing
    with-suggestions
    @add="baseAddStaff(event, $event)"
    @remove="baseRemoveStaff(event, $event)"
    @continue="confirmSave"
  />

  <TaskStepperDialog
    ref="addTaskDialog"
    mode="appointment"
    :loading="addingTask"
    :used-form-ids="usedFormIds"
    @save="onAddTask"
  />
</template>

<script>
import OverviewPartial from './panels/Overview'
import AppointmentPartial from 'components/app/calendar/partials/edit/panels/Appointment'
import ClientsPartial from './panels/Clients'
import StaffPartial from './panels/Staff'
import TypesPartial from './panels/Types.vue'
import ColorPartial from './panels/Color'
import AvailabilityDialog from './../availability/Dialog'
import RequiredFormSelector from 'components/app/settings/company/appointmentsTypes/partials/RequiredFormSelector.vue'
import ChecklistTemplateSelector from 'components/app/settings/company/appointmentsTypes/partials/ChecklistTemplateSelector.vue'
import TaskStepperDialog from 'components/app/checklists/TaskStepperDialog.vue'

import staffMixin from './../mixins/staffMixin'

export default {
  components: {
    OverviewPartial, AppointmentPartial, ClientsPartial, TypesPartial, StaffPartial, AvailabilityDialog, ColorPartial, RequiredFormSelector, ChecklistTemplateSelector, TaskStepperDialog
  },
  mixins: [staffMixin],
  props: {
    event: {
      type: Object,
      required: true
    },
    errors: {
      type: Object,
      default: () => {}
    },
    loading: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'add'
    },
    withoutClient: {
      type: Boolean,
      default: false
    },
    prefillDate: {
      type: Boolean,
      default: false
    },
    businessHours: {
      type: Object,
      default: null
    },
    hasUpdatedRule: {
      type: Boolean,
      default: false
    },
    hasDeletedRule: {
      type: Boolean,
      default: false
    },
    hasFutureRule: {
      type: Boolean,
      default: false
    },
    // Allow dialog to peek at calendar behind it
    enablePeeking: {
      type: Boolean,
      default: false
    },
    // Default state for send confirmation email checkbox
    sendConfirmationEmailDefault: {
      type: Boolean,
      default: false
    }
  },
  emits: ['hide', 'updateReference', 'save'],
  data () {
    return {
      dialogOpen: false,
      selectedTab: null,
      checkingAvailability: false,
      hasSeenStaffWarning: false,
      isPeeking: false,
      sendConfirmationEmail: false,
      addingTask: false
    }
  },
  computed: {
    // The appointment's existing tasks, flattened across its checklists
    currentTasks () {
      const requiredFormIds = this.event.required_form_template_ids || []
      const tasks = []
      const coveredFormIds = []
      for (const checklist of this.event.checklists || []) {
        const checklistName = checklist.template ? checklist.template.name : 'General'
        for (const item of checklist.items || []) {
          // Tasks auto-created from the appointment's required templates are
          // flagged so the link between the two systems is visible
          const itemFormIds = item.required_form_template_ids || []
          coveredFormIds.push(...itemFormIds)
          const fromRequiredForm = itemFormIds.some(id => requiredFormIds.includes(id))
          tasks.push({ ...item, checklistName, fromRequiredForm })
        }
      }

      // Preview the task each newly-required template will create on save, so
      // the required-template → task link is visible before saving
      for (const form of this.event.required_form_templates || []) {
        if (requiredFormIds.includes(form.id) && !coveredFormIds.includes(form.id)) {
          tasks.push({
            id: `pending-${form.id}`,
            text: `Complete ${form.title}`,
            is_completed: false,
            checklistName: 'General',
            fromRequiredForm: true,
            pending: true
          })
        }
      }

      return tasks
    },
    // Form template ids already linked to an existing task on this appointment,
    // used to stop the same form being added to a second task.
    usedFormIds () {
      const ids = []
      for (const checklist of this.event.checklists || []) {
        for (const item of checklist.items || []) {
          for (const formId of item.required_form_template_ids || []) {
            ids.push(formId)
          }
        }
      }
      return ids
    },
    modeTitle () {
      return this.mode === 'edit' ? 'Edit' : 'Add'
    },
    tabPanels () {
      return [
        { name: 'overview' },
        { name: 'types', title: 'Appointment Types' },
        { name: 'clients', title: 'Clients & Reminders' },
        { name: 'staff', title: 'Staff & Reminders' },
        { name: 'color', title: 'Colour' },
        { name: 'required-forms', title: 'Required Report Templates' },
        { name: 'checklist-templates', title: 'Checklist Templates and Tasks' },
        { name: null, title: 'More Options' }
      ]
    },
    steppedPanelOrder () {
      return ['types', 'clients', 'staff', null]
    },
    stepPercentageDone () {
      if (!this.selectedTab) {
        return 1
      }
      return (this.steppedPanelOrder.indexOf(this.selectedTab) + 1) / this.steppedPanelOrder.length
    },
    currentTab () {
      return this.tabPanels.find(item => item.name === this.selectedTab)
    },
    currentTabIsStepped () {
      return this.selectedTab && this.steppedPanelOrder.includes(this.selectedTab)
    },
    nextStep () {
      if (!this.selectedTab || !this.steppedPanelOrder.includes(this.selectedTab)) {
        return null
      }
      let tabIndex
      if ((tabIndex = this.steppedPanelOrder.indexOf(this.selectedTab)) >= this.steppedPanelOrder.length) {
        return null
      }
      const nextName = this.steppedPanelOrder[tabIndex + 1]
      return this.tabPanels.find(item => item.name === nextName)
    },
    // Reminders can only be altered if they have a specific appointment status
    // Otherwise don't show the options at all
    canAlterReminders () {
      return this.mode === 'add' || ['UNSCHEDULED', 'UNCONFIRMED', 'SCHEDULED'].includes(this.event.status)
    },
    staffLength () {
      return this.event.attendees.length
    },
    hasClients () {
      return this.event?.clients?.length > 0
    },
    canSendConfirmationEmail () {
      // Only allow confirmation emails for SCHEDULED appointments
      return this.event?.status === 'SCHEDULED'
    }
  },
  watch: {
    'event.start_date' () {
      this.hasSeenStaffWarning = false
    },
    'event.end_date' () {
      this.hasSeenStaffWarning = false
    },
    'staffLength' () {
      this.hasSeenStaffWarning = false
    },
    sendConfirmationEmailDefault: {
      immediate: true,
      handler (val) {
        this.sendConfirmationEmail = val && this.canSendConfirmationEmail
      }
    },
    canSendConfirmationEmail (val) {
      // Reset checkbox when it becomes disabled
      if (!val) {
        this.sendConfirmationEmail = false
      }
    }
  },
  methods: {
    // Icon + label for each of a task's requirements (photo, invoice, quote,
    // linked report templates), shown under the task in the current-tasks list
    taskRequirements (task) {
      const requirements = []
      if (task.requires_photo) {
        requirements.push({ icon: 'mdi-camera', label: 'Photo required' })
      }
      if (task.requires_invoice) {
        requirements.push({ icon: 'mdi-file-document-outline', label: 'Invoice required' })
      }
      if (task.requires_quotation) {
        requirements.push({ icon: 'mdi-file-document-edit-outline', label: 'Quote required' })
      }
      const forms = task.required_forms && task.required_forms.length > 0
        ? task.required_forms
        : (task.required_form_template_ids || []).map(id => {
            // Older payloads don't carry the form titles - resolve them from the
            // appointment's required templates where possible
            const template = (this.event.required_form_templates || []).find(t => t.id === id)
            return { id, title: template ? template.title : 'Linked report' }
          })
      for (const form of forms) {
        requirements.push({ icon: 'mdi-clipboard-text-outline', label: form.title })
      }
      return requirements
    },
    async onAddTask (payload) {
      this.addingTask = true
      try {
        const { status, payload: response } = await this.$post(
          `v1/appointments/${this.event.id}/checklists/items`,
          payload
        )
        if (status === 'success') {
          // The endpoint returns the (general) checklist with all of its items —
          // sync it into the event so the task list reflects the addition
          const checklists = this.event.checklists || []
          const index = checklists.findIndex(c => c.id === response.checklist.id)
          if (index >= 0) {
            checklists.splice(index, 1, response.checklist)
          }
          else {
            checklists.push(response.checklist)
          }
          this.event.checklists = checklists
          this.$refs.addTaskDialog.close()
        }
      }
      finally {
        this.addingTask = false
      }
    },
    open () {
      this.isPeeking = false

      if (this.dialogOpen) {
        return false
      }

      this.dialogOpen = true
      this.selectedTab = this.mode === 'add' ? 'types' : null
      return true
    },
    close () {
      this.dialogOpen = false
    },
    async save () {
      /* Check whether to prompt the user to verify the staff when submitting */
      if (!this.hasSeenStaffWarning && this.event.attendees.length > 0) {
        this.checkingAvailability = true
        const badResults = await this.$refs.availabilityDialog.open(true)
        this.checkingAvailability = false
        if (badResults) {
          this.$refs.availabilityDialog.confirm()
          return
        }
      }
      this.hasSeenStaffWarning = true
      this.confirmSave()
    },
    confirmSave () {
      this.$emit('save', { sendConfirmationEmail: this.sendConfirmationEmail })
    },
    onRequiredFormAdded (form) {
      // Add to the templates array for display
      if (!this.event.required_form_templates) {
        this.event.required_form_templates = []
      }
      const exists = this.event.required_form_templates.find(t => t.id === form.id)
      if (!exists) {
        this.event.required_form_templates.push({
          id: form.id,
          title: form.title,
          category: form.category,
          reference_id: form.reference_id,
          master_version: form.master_version,
          minor_version: form.minor_version
        })
      }
    },
    onRequiredFormRemoved (formId) {
      // Remove from the templates array
      if (this.event.required_form_templates) {
        this.event.required_form_templates = this.event.required_form_templates.filter(t => t.id !== formId)
      }
    }
  }
}
</script>

<style lang="scss">
.q-dialog.appointment-can-peek {
  & > .q-dialog__inner {
    transition: all var(--q-transition-duration) ease;
  }

  &.is-peeking {
    & > .q-dialog__inner {
      top: calc(100% - 50px);

      & > div {
        box-shadow: $shadow-up-5;
      }
    }
  }
}

body.platform-ios {
  &.q-ios-padding {
    .q-dialog.appointment-can-peek {
      &.is-peeking {
        & > .q-dialog__inner {
          padding-top: unset !important;
          padding-bottom: unset !important;
        }
      }
    }
  }

  .q-dialog.appointment-can-peek {
    & > .q-dialog__inner {
      & > div {
        transition: all var(--q-transition-duration);
      }
    }

    &.is-peeking {
      & > .q-dialog__inner {
        top: calc(100% - 50px - var(--safe-area-inset-bottom));

        & > div {
          padding-bottom: var(--safe-area-inset-bottom) !important;
        }

        &.q-dialog__inner--minimized {
          & > div {
            max-height: 100%;
          }
        }
      }
    }
  }
}
</style>
